import { useRef, useEffect, useLayoutEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import {
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { RichTextDisplay } from "@/components/common";

const DescriptionSection = ({ selectedDay, progress, sectionConfig }) => {
  const contentRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [processedContent, setProcessedContent] = useState("");

  // Update container width on mount and resize using useLayoutEffect
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (contentRef.current) {
        const width = contentRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    // Initial measurement
    updateWidth();

    // Add a slight delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(updateWidth, 0);

    window.addEventListener("resize", updateWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateWidth);
    };
  }, [selectedDay]);

  // Process content to format iframes
  useEffect(() => {
    if (!selectedDay?.description || typeof selectedDay.description !== "string") {
      setProcessedContent(selectedDay?.description || "");
      return;
    }

    // Process HTML content to update iframe dimensions
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = selectedDay.description;

    const iframes = tempDiv.querySelectorAll("iframe");
    if (iframes.length > 0 && containerWidth > 0) {
      iframes.forEach((iframe) => {
        // YouTube aspect ratio is 16:9
        const width = containerWidth;
        const height = Math.round((width * 9) / 16);

        iframe.setAttribute("width", width);
        iframe.setAttribute("height", height);
        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
        iframe.style.maxWidth = "100%";
      });
    }

    setProcessedContent(tempDiv.innerHTML);
  }, [selectedDay, containerWidth]);

  const isCompleted = progress?.partProgress?.description;

  return (
    <Box>
      {/* Section Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {sectionConfig.name}: {selectedDay.title}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Chip
              icon={<BookIcon />}
              label={`DAY ${selectedDay.dayNumber}`}
              color="warning"
              sx={{ fontWeight: "bold" }}
            />
            <Chip
              label={selectedDay.title}
              variant="outlined"
              sx={{ color: "white", borderColor: "white" }}
            />
            {isCompleted && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Completed"
                color="success"
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }} ref={contentRef}>
        <RichTextDisplay 
          content={processedContent || "Content for this section will be displayed here."}
        />
      </Paper>
    </Box>
  );
};

export default DescriptionSection;
